<script lang="ts">
    import type {AxiosInstance} from 'axios';
    import {getContext} from 'svelte';

    import Button from 'general/components/button/Button.svelte';
    import Input from '../helpers/input/Input.svelte';

    import {modal, dyData} from 'general/store';
    import {CODE, ANALYTICS, TEMPLATE} from 'general/constants';
    import {pushPopupEvent} from 'general/libs/pushDataLayer';
    import {emailSubscribe} from 'general/libs/dy/emailSubscribe';
    import {key} from 'general/context';
    import {logWithSentry} from 'sentry/sentry';

    import type {ContextType} from 'types/general';
    import type {NewsLetterSuccessResponseEmailType} from 'types/newsletter';
    import RadioButton from '../helpers/radioButton/RadioButton.svelte';
    import {handleSubscribe} from 'general/components/form/email/handleSubscribe';

    import s from '../style.module.css';

    export const onClick: () => void = () => {};
    export let instance: AxiosInstance;
    export let offerGiven = '';
    export let label = 'Enter email address';
    export let marginBottom = '12px';
    export let checkBoxMarginBottom = $dyData.radioButton?.marginBottom || '25px';
    export let errorMessage = '';
    export let template: (typeof TEMPLATE)[keyof typeof TEMPLATE] = TEMPLATE.CIRCLE;

    let isProcessing = false;

    const isCart = $dyData.template === TEMPLATE.SQUARE_EXIT_CART;

    const {onEmailSubscribed, onEmailRejected} = getContext<ContextType>(key);

    const handleInput = ({target}: Event) => {
        modal.changeEmail((target as HTMLInputElement).value);
        if (errorMessage) {
            isProcessing = false;
            errorMessage = '';
        }
    };

    const handleChangeRadioButton = ({target}: Event) => {
        modal.changeRadioValue((target as HTMLInputElement).value);
    };

    const handleSubmit = () => {
        if (errorMessage) {
            isProcessing = true;
            return;
        }

        const affiliateId = localStorage.getItem('gusa_aff_id');
        const refId = $modal.refId || localStorage.getItem('cordialRefId');

        try {
            isProcessing = true;

            emailSubscribe<NewsLetterSuccessResponseEmailType>(instance, {
                email: $modal.email,
                affiliateId,
                offerGiven,
                refId,
            })
                .then(data => {
                    onEmailSubscribed(data?.data);

                    if (data.status === CODE.SUCCESSFUL) {
                        handleSubscribe({
                            instance,
                            subscribeEvent: ANALYTICS.POPUP_SUBSCRIBED,
                            code: data.status,
                            isCart,
                        });
                    }
                })
                .catch(({response: {data}}) => {
                    // TODO: GUSA-13495 requires implementation without using CustomEvent
                    window.dispatchEvent(
                        new CustomEvent('dynamicYield:newsletter', {
                            detail: {
                                type: 'onError',
                                payload: {
                                    email: $modal.email,
                                    code: data.code,
                                },
                            },
                        }),
                    );

                    onEmailRejected();

                    if (
                        data.code === CODE.PREVIOUSLY_SUBSCRIBED ||
                        data.code === CODE.ALREADY_SUBSCRIBED
                    ) {
                        handleSubscribe({
                            subscribeEvent: 'Error: This email is subscribed.',
                            code: data.code,
                            isCart,
                            instance,
                        });
                    } else if ('message' in data) {
                        pushPopupEvent(`Error: ${data.message}`);
                        errorMessage = data.message;
                    } else {
                        pushPopupEvent(`Error: Something went wrong.`, {email: $modal.email});
                        errorMessage = `Something went wrong.`;
                    }
                })
                .finally(() => {
                    isProcessing = false;
                });
        } catch (error) {
            logWithSentry(error as Error);
            modal.close();
        }
    };
</script>

<form
    name="popupEmail"
    class="{s.form} {s[template] || ''} {s[$modal.state] || ''}"
    on:submit|preventDefault={handleSubmit}
    class:smallScreen={$dyData.isSmallerScreen}
>
    <RadioButton
        handleChange={handleChangeRadioButton}
        {template}
        marginBottom={checkBoxMarginBottom}
    />
    <Input
        bind:inputValue={$modal.email}
        type="email"
        pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
        minlength={8}
        maxlength={254}
        {label}
        required
        isVisibleLabel
        withIcon={isCart}
        {handleInput}
        {marginBottom}
        reminder={errorMessage}
        reminderStatus="error"
        {template}
    />

    <!-- We can't use common button cuz Button changes it's props before animation ends -->
    {#if template === TEMPLATE.SQUARE_DESKTOP_COMMON}
        <button class="{s.button} {s[template] || ''} {s.main}" disabled={isProcessing}>
            {$dyData.ctaButtonText?.email || 'Send Me a Coupon'}
        </button>
    {:else}
        <Button
            {template}
            children={$dyData.ctaButtonText?.email || 'Send Me a Coupon'}
            disabled={isProcessing}
            dyButtonData={$dyData.mainButton?.main}
        />
    {/if}
</form>
